import React, { FC } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'

export type AllotmentAdvanceAttachment = {
  id: number
  fileName: string
  filePath?: string | null
  fileType?: string | null
  fileSize?: number | null
  mimeType?: string | null
}

export type AllotmentAdvanceRecord = {
  id: number
  crewId: number
  crewName: string
  rankName?: string
  vesselName?: string
  crewActive?: boolean
  allotmentExists?: boolean
  canManageAllotmentDraft?: boolean
  companyGroupAdminId?: number
  companyAdminId?: number
  vesselId?: number
  branchName?: string
  branchCode?: string
  bankName?: string
  accountNumber?: string
  ifscRtgsCode?: string
  ifscRtgsType?: string
  swiftIandi?: string
  bankAddress?: string
  salaryCurrency?: string
  salaryOtherCurrency?: string
  beneficiaryName?: string
  carryForwardAmount?: number
  wagesEarned?: number
  allotmentAmount?: number
  balanceBroughtForward?: number
  remarks?: string
  statementMonth?: string
  workflowStatus?: 'DRAFT' | 'PENDING_MASTER_REVIEW' | 'MASTER_REVIEWED' | 'REVIEWED' | 'SUBMITTED'
  officeRemarks?: string
  masterRemarks?: string
  balanceCfLastMonth?: number
  totalClaim?: number
  totalDeduction?: number
  totalAllotmentAmount?: number
  balanceWages?: number
  attachments?: AllotmentAdvanceAttachment[]
  attachmentCount?: number
  createdAt?: string
}

type Props = {
  visible: boolean
  record: AllotmentAdvanceRecord | null
  onClose: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL_SMALL = 'fw-semibold text-muted d-block mb-1'

const resolveCurrencyCode = (salaryCurrency?: string, salaryOtherCurrency?: string) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).formatToParts(0).find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const formatCurrency = (value?: number) =>
  typeof value === 'number'
    ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-'

const ViewAllotmentAdvanceModal: FC<Props> = ({ visible, record, onClose }) => {
  if (!visible || !record) return null

  const currencySymbol = getCurrencySymbol(resolveCurrencyCode(record.salaryCurrency, record.salaryOtherCurrency))
  const formatAmountWithCurrency = (value?: number) =>
    typeof value === 'number' ? `${currencySymbol}${formatCurrency(value)}` : '-'

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '70vw', height: '70vh', maxWidth: '1050px', maxHeight: '800px' }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <div className='d-flex flex-column'>
            <h5 className='modal-title text-dark m-0'>
              Allotment & Advance - {record.crewName}
            </h5>
            <div className='text-muted fs-7'>
              {record.rankName ? `Rank: ${record.rankName}` : ''}
              {record.vesselName ? (
                <>
                  {' | '}
                  <span>Vessel: </span>
                  <VesselLandingLink
                    vesselId={record.vesselId ?? null}
                    vesselName={record.vesselName}
                    className='fw-semibold text-dark text-hover-primary'
                  />
                </>
              ) : ''}
            </div>
          </div>
          <button className='btn btn-sm btn-light' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2' />
          </button>
        </div>

        <div className='flex-grow-1 overflow-auto px-4 py-4'>
          <div className='row g-4'>
            <div className='col-12'>
              <div className='border rounded p-4 h-100'>
                <div className='fw-bold mb-3'>Allotment Summary</div>
                <div className='row g-3'>
                  <div className='col-md-4'>
                    <span className={LABEL_SMALL}>Balance C/F Last Month</span>
                    <div className='fw-semibold'>{formatAmountWithCurrency(record.balanceCfLastMonth)}</div>
                  </div>
                  <div className='col-md-4'>
                    <span className={LABEL_SMALL}>Wages Earned</span>
                    <div className='fw-semibold'>{formatAmountWithCurrency(record.wagesEarned)}</div>
                  </div>
                  <div className='col-md-4'>
                    <span className={LABEL_SMALL}>Total Claim</span>
                    <div className='fw-semibold'>{formatAmountWithCurrency(record.totalClaim)}</div>
                  </div>
                  <div className='col-md-4'>
                    <span className={LABEL_SMALL}>Total Deduction</span>
                    <div className='fw-semibold'>{formatAmountWithCurrency(record.totalDeduction)}</div>
                  </div>
                  <div className='col-md-4'>
                    <span className={LABEL_SMALL}>Total Allotment Amount</span>
                    <div className='fw-semibold'>{formatAmountWithCurrency(record.totalAllotmentAmount)}</div>
                  </div>
                  <div className='col-md-4'>
                    <span className={LABEL_SMALL}>Balance Wages</span>
                    <div className='fw-bold'>{formatAmountWithCurrency(record.balanceWages)}</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className='border-top px-4 py-3 d-flex justify-content-end'>
          <button className='btn btn-light' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewAllotmentAdvanceModal

